import { Box, Button, ButtonProps, Group, List, Popover, ThemeIcon } from '@mantine/core';
import { DatePicker, DatePickerValue, DatesRangeValue } from '@mantine/dates';
import { IconCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SvgCalender } from '@/components/Dashboard/icon';
import { useDateLocal } from '@mygpt/app';
import { useClickOutside } from '@mantine/hooks';

type DatePickerRangeValue = DatePickerValue<'range'>;

interface DateSelectorProps extends ButtonProps {
  dateRange?: DatesRangeValue;
  onDateChange: (date: DatesRangeValue | null, text: string) => void;
  hasAll?: boolean;
  showCustomDateTime?: boolean;
}

interface CustomListItemProps {
  buttonText: string;
  checked?: boolean;
  onClick?: () => void;
}

const Check = () => (
  <ThemeIcon size={16} color="violet.5">
    <IconCheck />
  </ThemeIcon>
);

const DateSelector: React.FC<DateSelectorProps> = ({
  dateRange,
  hasAll,
  onDateChange,
  showCustomDateTime,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const { dateLocale } = useDateLocal();

  const DateOptions = [
    { text: t('dashboard.date.previous_days', { size: 7 }), value: 7 },
    { text: t('dashboard.date.previous_days', { size: 30 }), value: 30 },
    { text: t('dashboard.date.previous_days', { size: 180 }), value: 180 },
    { text: t('dashboard.date.custom_date'), value: -1 },
  ];

  if (hasAll) {
    DateOptions.unshift({
      text: t('all'),
      value: -2,
    });
  } else {
    DateOptions.unshift(
      { text: t('dashboard.date.today'), value: 0 },
      { text: t('dashboard.date.yesterday'), value: 1 }
    );

    DateOptions.splice(DateOptions.length - 1, 0, {
      text: t('dashboard.date.previous_days', { size: 365 }),
      value: 365,
    });
  }

  const [isOpen, setIsOpen] = useState(false);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const ref = useClickOutside(() => setIsOpen(false));
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [isHandleSelectDate, setIsHandleSelectDate] = useState<boolean>(false);

  const [customDate, setCustomDate] = useState<DatePickerRangeValue>([new Date(2021, 11, 1), new Date(2021, 11, 5)]);

  const getDateText = (date: number) => {
    return DateOptions.find((item) => item.value === date)?.text || '';
  };

  const dateText = useMemo(() => {
    if (selectedDate === null) return t('dashboard.date.select');
    if (showCustomDateTime && selectedDate === -1 && !isCustomOpen) {
      const text = customDate.map((date) => dayjs(date).format('YYYY-MM-DD'));
      if (text[0] === text[1]) return text[0];
      return text.join(' ~ ');
    }
    return getDateText(selectedDate);
  }, [selectedDate, t, isCustomOpen]);

  const handleSelectDate = (date: number) => {
    // custom date
    setSelectedDate(date);
    setIsHandleSelectDate(true);
    setTimeout(() => {
      setIsHandleSelectDate(false);
    }, 200);
    if (date === -1) {
      setIsCustomOpen(!isCustomOpen);
      return;
    }

    setIsOpen(false);
    const dateRange = date === -2 ? null : computeDateRange(date);
    const dateRangeText = getDateText(date);
    onDateChange(dateRange, dateRangeText);
  };

  useEffect(() => {
    typeof selectedDate === 'number' && onDateChange(null, getDateText(selectedDate));
  }, [t]);

  const handleCustomDateChange = (dates: DatesRangeValue) => {
    setCustomDate(dates);

    // select done
    if (dates.filter((item) => !!item).length > 1) {
      setSelectedDate(-1);
      setIsOpen(false);
      setIsCustomOpen(false);
      const dateRangeText = getDateText(-1);
      if (dates[1]) {
        const endDate = new Date(dates[1]);
        endDate.setHours(23, 59, 59, 999);
        dates[1] = endDate;
      }
      onDateChange(dates, dateRangeText);
    }
  };

  const computeDateRange = (days: number): [Date, Date] => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set the time to the start of today

    let endDate: Date;
    if (days === 0) {
      // If days is 0, then set endDate to the end of today
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
    } else {
      // Compute end date: it should be end of yesterday
      endDate = new Date(now);
      endDate.setDate(endDate.getDate() - 1); // Set to end of yesterday
      endDate.setHours(23, 59, 59, 999);
    }

    // Compute start date based on the number of days
    const startDate = new Date(endDate);
    if (days) {
      startDate.setDate(startDate.getDate() - days + 1); // Subtract the specified number of days and add 1 day to include the start day itself
    }
    startDate.setHours(0, 0, 0, 0); // Set the time to the start of the day

    return [startDate, endDate];
  };

  useEffect(() => {
    dateRange && dateRange[0] && dateRange[1] && !isHandleSelectDate && setSelectedDate(-1);
  }, [dateRange]);

  // select default value
  useEffect(() => {
    !dateRange && handleSelectDate(hasAll ? -2 : 7);
  }, []);

  const CustomListItem: React.FC<CustomListItemProps> = ({ buttonText, checked, onClick }) => {
    return (
      <List.Item onClick={onClick} className="px-5 py-2 cursor-pointer hover:bg-gray-100">
        <Group>
          <span className={`${checked && 'text-violet-600'}`}>{buttonText}</span>
          {checked && <Check />}
        </Group>
      </List.Item>
    );
  };

  return (
    <Popover opened={isOpen} onDismiss={() => setIsOpen(false)} position="bottom" shadow="md" radius="lg">
      <Popover.Target>
        <Button
          onClick={() => setIsOpen(!isOpen)}
          variant={'outline'}
          leftSection={<SvgCalender />}
          h={'auto'}
          p={16}
          radius={12}
          {...buttonProps}
        >
          {dateText}
        </Button>
      </Popover.Target>
      <Popover.Dropdown style={{ padding: 0 }} ref={ref}>
        {!isCustomOpen ? (
          <List>
            {DateOptions.map((item, index) => {
              const { value } = item;
              return (
                <CustomListItem
                  key={index}
                  buttonText={item.text}
                  checked={selectedDate === value}
                  onClick={() => handleSelectDate(value)}
                />
              );
            })}
          </List>
        ) : (
          <Box p={10}>
            <DatePicker
              type="range"
              numberOfColumns={2}
              value={customDate}
              onChange={handleCustomDateChange}
              locale={dateLocale}
              allowSingleDateInRange
            />
          </Box>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default DateSelector;
